import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { isProductOutOfStock } from '../../helpers/product-availability.helper';
import { ContextResponse, ProductResponse } from '@woolworthsnz/trader-api';
import { PromotionalCarouselResponseModel } from '../../models/PromotionalCarouselResponseModel';
import { StampGridComponent } from '../stamp-grid/stamp-grid.component';
import {
	CarouselDirective,
	CarouselComponent,
	CardComponent,
	FeatureService,
	TealiumEventEnum,
	SearchTarget,
	TrackImpressionDirective,
} from '@woolworthsnz/styleguide';
import { NgIf, NgFor } from '@angular/common';
import { TealiumUtagService } from '@woolworthsnz/analytics';
import { filter, take } from 'rxjs';
import { extractQLCodeFromCtaName } from '../../helpers/product-response.helper';

@Component({
	selector: 'product-content-in-grid',
	template: `
		<a [href]="content?.link" [title]="content?.mobileImage?.alt" (click)="handleClick()">
			<figure class="contentInGrid-mobileImage">
				<img [src]="content?.mobileImage?.src" [alt]="content?.mobileImage?.alt" /></figure
		></a>
		<div class="contentInGrid">
			<a [href]="content?.link" [title]="content?.image?.alt" (click)="handleClick()">
				<div class="contentInGrid-advertisement">
					<figure>
						<img [src]="content?.image?.src" [alt]="content?.image?.alt" />
					</figure>

					<div class="contentInGrid-advertisementCopy" *ngIf="content?.headingText && content?.paragraphText">
						<h4 *ngIf="content?.headingText">
							{{ content?.headingText }}
						</h4>
						<p *ngIf="content?.paragraphText">
							{{ content?.paragraphText }}
						</p>
					</div>
				</div>
			</a>
			<div class="contentInGrid-carousel">
				<cdx-carousel
					[showPagination]="false"
					[contentChange]="content"
					(prevClickEvent)="handlePrevClick($event.currentPage)"
					(nextClickEvent)="handleNextClick($event.currentPage)"
				>
					<div cdxCarousel>
						<ng-container
							*ngFor="let product of content?.products; trackBy: trackByProductSku; let i = index"
						>
							<cdx-card *ngIf="!productIsOutOfStock(product)">
								<product-stamp-grid
									*ngIf="!productIsOutOfStock(product)"
									[isGridCarousel]="true"
									[product]="product"
									[position]="gridPosition"
									[cppItemCount]="cppItemCount"
									[uniqueid]="product.sku"
									[carouselindex]="i"
									[target]="target"
									[promotion]="content"
									(click)="onClickStampGridComponent(i + 1, product)"
								>
								</product-stamp-grid>
							</cdx-card>
						</ng-container>
					</div>
				</cdx-carousel>
			</div>
		</div>
	`,
	styleUrls: ['./content-in-grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		CarouselComponent,
		CarouselDirective,
		NgFor,
		CardComponent,
		StampGridComponent,
		TrackImpressionDirective,
	],
})
export class ContentInGridComponent implements OnInit, OnChanges {
	@Output() advertisementClick: EventEmitter<PromotionalCarouselResponseModel> = new EventEmitter();
	@Output() init: EventEmitter<PromotionalCarouselResponseModel> = new EventEmitter();

	@Input() public content?: PromotionalCarouselResponseModel;
	@Input() gridPosition: number;
	@Input() cppItemCount = 0;
	@Input() target?: string;

	private promoEvent = 'promo-element-ready-for-default-impression-observability';
	private elementName = 'product-content-in-grid';
	private isCartologyGA4EventTracking = false;

	constructor(
		private tealiumUtagService: TealiumUtagService,
		private featureService: FeatureService
	) {}

	productIsOutOfStock(product: ProductResponse): boolean {
		return isProductOutOfStock(product);
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.content = changes.content.currentValue;
	}

	ngOnInit(): void {
		this.init.emit(this.content);
		this.featureService
			.isEnabled(ContextResponse.EnabledFeaturesEnum.EnableCartologyGA4EventTracking)
			.pipe(
				filter((isEnabled) => Boolean(isEnabled)),
				take(1)
			)
			.subscribe((isCartologyGA4EventTracking) => {
				this.isCartologyGA4EventTracking = isCartologyGA4EventTracking;
				if (document.body.classList.contains('cartology-gam-ad')) {
					// skip impression tracking for cartology-gam-ad
					this.registerEventListenerToResumeTracking();
				} else {
					this.submitGA4Impression(TealiumEventEnum.ViewPromotion);
				}
				this.submitCIGImpression();
			});
	}

	private registerEventListenerToResumeTracking(): void {
		window.addEventListener(this.promoEvent, (event: Event) => {
			const customEvent = event as CustomEvent;
			const eventElement = customEvent.detail?.element;

			if (!eventElement || eventElement === this.elementName) {
				this.submitGA4Impression(TealiumEventEnum.ViewPromotion);
			}
		});
	}

	handleClick(): void {
		this.advertisementClick.emit(this.content);
		this.submitGA4Impression(TealiumEventEnum.SelectPromotion);
	}

	trackByProductSku = (index: number, product: ProductResponse): string | undefined => product.sku;

	onClickStampGridComponent(index: number, product: ProductResponse): void {
		if (this.isCartologyGA4EventTracking && this.content?.products !== undefined) {
			this.tealiumUtagService.link({
				tealium_event: TealiumEventEnum.SelectItem,
				ecommerce: {
					item_list_id: extractQLCodeFromCtaName(this.content?.name) ?? this.content?.id,
					item_list_name: this.content?.name,
					items: [
						{
							item_id: product.sku,
							item_name: product.name,
							discount: product.price?.savePrice,
							index: parseFloat(`${this.content?.positionIndex}.${index}`),
							item_brand: product.brand,
							item_category: product.departments?.[0]?.name ?? '',
							item_variant: product.type,
							price: product.price?.salePrice,
							availability_status: product.availabilityStatus,
							promotion_id: extractQLCodeFromCtaName(this.content?.name) ?? this.content?.id,
							promotion_name: this.content?.name,
						},
					],
				},
			});
		}
	}

	handlePrevClick(currentPage: number): void {
		if (this.content?.products && currentPage > 0) {
			this.triggerProductImpression(currentPage - 1);
		}
	}

	handleNextClick(currentPage: number): void {
		if (this.content?.products) {
			this.triggerProductImpression(currentPage - 1);
		}
	}

	private submitCIGImpression(): void {
		if (this.content?.products && this.content.products.length > 0) {
			this.triggerProductImpression(0);
		}
	}

	private triggerProductImpression(productIndex: number): void {
		if (this.content?.products) {
			const creativeName = this.determineCreativeName();
			const product = this.content.products[productIndex];

			const item = {
				item_id: product.sku,
				item_name: product.name,
				coupon: '',
				discount: product.price?.savePrice,
				index: parseFloat(`${this.content?.positionIndex}.${productIndex + 1}`),
				item_brand: product.brand,
				item_category: product.departments?.[0]?.name ?? '',
				item_category_2: product.departments?.[1]?.name ?? '',
				item_category_3: product.departments?.[2]?.name ?? '',
				item_list_id: 'n/a',
				item_list_name: 'n/a',
				item_variant: product.type,
				price: product.price?.salePrice,
				quantity: product.quantity?.value,
				availability_status: product.availabilityStatus,
				each_kg: product.unit,
				promotion_id: extractQLCodeFromCtaName(this.content?.name) ?? this.content?.id,
				promotion_name: this.content?.name,
			};

			this.tealiumUtagService.view({
				tealium_event: 'view_item_list',
				ecommerce: {
					item_list_id: (productIndex + 1).toString(),
					item_list_name: creativeName,
					items: [item],
				},
			});
		}
	}

	private submitGA4Impression(event: string): void {
		if (this.isCartologyGA4EventTracking && this.content?.products) {
			const creativeName = this.determineCreativeName();
			const items = this.content.products.map((product: ProductResponse, index) => ({
				item_id: product.sku,
				item_name: product.name,
				index: parseFloat(`${this.content?.positionIndex}.${index + 1}`),
				promotion_product_tags: [product.productTag?.tagType?.toString()],
				discount: product.price?.savePrice,
				item_brand: product.brand,
				item_category: product.departments?.[0]?.name ?? '',
				item_category_2: product.departments?.[1]?.name ?? '',
				item_category_3: product.departments?.[2]?.name ?? '',
				item_list_id: 'n/a',
				item_list_name: 'n/a',
				item_variant: product.type,
				price: product.price?.salePrice,
				availability_status: product.availabilityStatus,
				has_shopper_notes: product.shopperNotes !== undefined,
			}));

			this.tealiumUtagService.view({
				tealium_event: event,
				ecommerce: {
					creative_name: creativeName,
					creative_slot: this.content?.positionIndex,
					promotion_id: extractQLCodeFromCtaName(this.content?.name) ?? this.content?.id,
					promotion_name: this.content.name,
					items,
				},
			});
		}
	}

	private determineCreativeName(): string {
		switch (this.target) {
			case SearchTarget.Search:
				return 'Search CIG Carousel';
			case SearchTarget.Browse:
				return 'Browse CIG Carousel';
			default:
				return '';
		}
	}
}
